import { Filters, PriceRange, StaticPrice } from '@/types/priceList';

export const defaultFilters = {
  search: '',
  showNew: true,
  showUpdated: true,
  showDeleted: true,
  showWarnings: true,
  showErrors: true,
  showUnchanged: true,
  menuOpen: false,
};

export const emptyPriceList = {
  id: '',
  name: '',
  tag: '',
  startDate: null,
  endDate: null,
  partners: [],
  endUsers: [],
  endUserCaseSensitive: false,
  endUserExactMatch: false,
  file: null,
  appendMode: true,
  copperPrice: null,
  notes: '',
};

export const deleteMetadata = (entry: any) => ({
  ...entry,
  id: undefined,
  deleted: false,
  createdAt: undefined,
  updatedAt: undefined,
  priceListId: undefined,
});

// Meters per unit, exact by definition. Expressed this way (rather than as units
// per meter) so `toMeters` is the same multiplication the backend performs — a
// rounded reciprocal such as 3.28084 disagrees with 0.3048 in the 8th digit, and
// the backend's formula guard drops a base/adder whose price is off by a cent.
const METERS_PER_UNIT = {
  F: 0.3048,
  CM: 0.01,
  I: 0.0254,
  M: 1,
};

export const convertLength = (length, unit, round = false, toMeters = true) => {
  const convertedLength = toMeters ? length * METERS_PER_UNIT[unit] : length / METERS_PER_UNIT[unit];
  return round ? parseFloat(convertedLength.toFixed(6)) : convertedLength;
};

export const roundStringToDecimals = (value, decimals) => {
  if (isNaN(value) || isNaN(decimals) || decimals < 0) return value;

  const factor = Math.pow(10, decimals);
  const roundedValue = Math.round(parseFloat(value) * factor) / factor;

  // 1.200000 becomes '1.2'
  return parseFloat(roundedValue.toFixed(decimals)).toString();
};

/**
 * Format a date string to 'YYYY-MM-DD' format
 */
export const formatDate = (dateString) => {
  if (!dateString) {
    return null;
  }

  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

export const isNumber = (value) => {
  return (!!value && !isNaN(value)) || 'Value cannot be empty';
};

export const isPositiveNumber = (value) => {
  return (!!value && value > 0) || 'Value must be greater than 0';
};

export const isValidUnit = (value) => {
  return ['M', 'CM', 'I', 'F'].includes(value) || 'Unit must be M, CM, I, or F';
};

export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return true;
  }
  return new Date(startDate) < new Date(endDate) || 'Start date must be before end date';
};

export const rangesOverlap = (range1: PriceRange, range2: PriceRange) => {
  const range1Min = convertLength(range1.minLengthInMeters, range1.displayLengthUnit);
  const range1Max = convertLength(range1.maxLengthInMeters, range1.displayLengthUnit);
  const range2Min = convertLength(range2.minLengthInMeters, range2.displayLengthUnit);
  const range2Max = convertLength(range2.maxLengthInMeters, range2.displayLengthUnit);

  return Math.max(range1Min, range2Min) < Math.min(range1Max, range2Max);
};

export const applyFilters = (key: string, filters: Filters, diff) => {
  const isNew = diff.new.has(key);
  const isUpdated = diff.updated.has(key);
  const isDeleted = diff.deleted.has(key);
  const isUnchanged = !isNew && !isUpdated && !isDeleted;
  const hasWarnings = diff.warnings.has(key);
  const hasErrors = diff.errors.has(key);

  // Check if key contains the search text (case insensitive)
  const matchesSearch = !filters.search || key.toLowerCase().includes(filters.search.toLowerCase());

  return (
    matchesSearch &&
    ((filters.showNew && isNew) ||
      (filters.showUpdated && isUpdated) ||
      (filters.showDeleted && isDeleted) ||
      (filters.showWarnings && hasWarnings) ||
      (filters.showErrors && hasErrors) ||
      (filters.showUnchanged && isUnchanged))
  );
};

export const trimPN = (pn) => {
  /**
   * Remove all spaces, replace en/em dashes with hyphen, remove trailing hyphens
   */
  return pn.replace(/\s/g, '').replace(/[–—]/g, '-').replace(/[-]+$/, '').replace(/--/g, '-').toUpperCase();
};

export const getPriceRangesDiffs = (newRanges, persisted) => {
  const updated = new Map();
  const created = new Map();
  const deleted = new Map();

  newRanges.forEach((ranges, partNumber) => {
    const key = partNumber;
    if (!persisted.has(key)) {
      created.set(key, ranges);
    } else {
      const persistedRangesForThisPartNumber = persisted.get(key);
      const newRangesForThisPartNumber = newRanges.get(key);
      if (persistedRangesForThisPartNumber.length !== newRangesForThisPartNumber.length) {
        // If current length is 0, means it's deleted, so not updated
        if (newRangesForThisPartNumber.length === 0) {
          deleted.set(key, persistedRangesForThisPartNumber);
        } else {
          updated.set(key, newRangesForThisPartNumber);
        }
      } else {
        newRangesForThisPartNumber.forEach((newRange) => {
          const identicalRange = findRangeInList(persistedRangesForThisPartNumber, newRange);
          if (!identicalRange) {
            updated.set(key, [...persistedRangesForThisPartNumber, newRange]);
          }
        });
      }
    }
  });

  persisted.forEach((ranges, partNumber) => {
    const key = partNumber;
    const newRange = newRanges.get(key);
    if (!newRange) {
      deleted.set(key, ranges);
    }
  });

  return { updated, created, deleted };
};

export const getStaticPricesDiffs = (newStaticPrices: StaticPrice[], persisted: Map<string, StaticPrice>) => {
  const updated = new Map<string, StaticPrice>();
  const created = new Map<string, StaticPrice>();
  const deleted = new Map<string, StaticPrice>();

  newStaticPrices.forEach((staticPrice) => {
    const key = `${staticPrice.id}-${staticPrice.partNumber}`;
    const persistedPrice = Array.from(persisted.values()).find((p) => p.partNumber === staticPrice.partNumber);

    if (!persistedPrice) {
      created.set(key, staticPrice);
    } else if (persistedPrice.price !== staticPrice.price) {
      updated.set(key, staticPrice);
    }
  });

  persisted.forEach((price, partNumber) => {
    const key = `${price.id}-${price.partNumber}`;
    const newPrice = newStaticPrices.find((staticPrice) => staticPrice.partNumber === partNumber);
    if (!newPrice) {
      deleted.set(key, price);
    }
  });

  return { updated, created, deleted };
};

export const getTrustedSeriesDiffs = (newSeries, persisted) => {
  const updated = new Map();
  const created = new Map();
  const deleted = new Map();

  newSeries.forEach((series, partNumber) => {
    const key = partNumber;
    if (!persisted.has(key)) {
      created.set(key, series);
    } else {
      const persistedSeriesForThisPartNumber = persisted.get(key);
      const newSeriesForThisPartNumber = newSeries.get(key);
      if (persistedSeriesForThisPartNumber.length !== newSeriesForThisPartNumber.length) {
        // If current length is 0, means it's deleted, so not updated
        if (newSeriesForThisPartNumber.length === 0) {
          deleted.set(key, persistedSeriesForThisPartNumber);
        } else {
          updated.set(key, newSeriesForThisPartNumber);
        }
      } else {
        newSeriesForThisPartNumber.forEach((newSerie) => {
          const identicalSerie = persistedSeriesForThisPartNumber.find(
            (persistedSerie) =>
              persistedSerie.lengthInMeters === newSerie.lengthInMeters &&
              persistedSerie.displayLengthUnit === newSerie.displayLengthUnit &&
              persistedSerie.price === newSerie.price,
          );
          if (!identicalSerie) {
            updated.set(key, [...persistedSeriesForThisPartNumber, newSerie]);
          }
        });
      }
    }
  });

  persisted.forEach((series, partNumber) => {
    const key = partNumber;
    const isSeriesInNewSeries = newSeries.get(key);
    if (!isSeriesInNewSeries) {
      deleted.set(key, series);
    }
  });

  return { updated, created, deleted };
};

export const getErrorsMapping = (errorCollection: Map<string, any[]>) => {
  const errors: Record<string, string> = {};

  errorCollection.forEach((allErrors) => {
    allErrors.forEach((error) => {
      // If error is an object and contains id, add it to the mapping
      errors[error.id] = error.message;
    });
  });
  return errors;
};

export const deepCopyMap = (originalMap) => {
  // Convert Map to an array of [key, value] pairs
  const arr = Array.from(originalMap);
  const copiedArr = JSON.parse(JSON.stringify(arr));
  return new Map(copiedArr);
};

export const findRangeInList = (list, range) => {
  return list.find(
    (item) =>
      item.minLengthInMeters === range.minLengthInMeters &&
      item.maxLengthInMeters === range.maxLengthInMeters &&
      item.perMeterAdder === range.perMeterAdder &&
      item.basePrice === range.basePrice,
  );
};

// Converts time to Aurora TimeZone and returns a date object (with wrong time, only date is correct)
export const getDateInAuroraTimeZone = (date: Date | string) => {
  return new Date(new Date(date).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }));
};
